import { History } from 'history';
import { Store } from 'repatch';

import State from '../core/State';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import PerformanceReportWebApi from '../service/persistent/PerformanceReportWebApi';
import ViewVisibilityStorage from '../service/persistent/ViewVisibilityStorage';
import { disposeProjectAndCreations } from '../thunk/project';
import {
  finishFetchingDailySummaryPerformance,
  finishFetchingPeriodicDetailPerformance,
  finishFetchingPeriodicImprovementPerformance,
  setDailySummaryPerformance,
  setHasLocalProject,
  setPerformanceReports,
  setPeriodicDetailPerformance,
  setPeriodicImprovementPerformance,
  setReportingRequest,
  startFetchingDailySummaryPerformance,
  startFetchingPeriodicDetailPerformance,
  startFetchingPeriodicImprovementPerformance,
} from '../thunk/projectReporting';
import { computeMetricsValues } from '../utility/extractPeriodicImprovementPerformance';
import {
  PerformanceReport,
  PerformanceReportDetailRequest,
  PerformanceReportDimension,
  PerformanceReportImprovementRequest,
  PerformanceReportMetric,
  PerformanceReportNetwork,
  PerformanceReportSummaryRequest,
} from './../generated/graphql';

class ShowPerformanceReportUsecase {
  private store: Store<State>;
  private history: History;
  private performanceReportWebApi: PerformanceReportWebApi;
  private messageDispatcher: MessageDispatcher;
  private errorPresenter: ErrorPresenter;
  private viewVisibilityStorage: ViewVisibilityStorage;

  constructor({
    store,
    history,
    performanceReportWebApi,
    messageDispatcher,
    errorPresenter,
    viewVisibilityStorage,
  }: {
    store: Store<State>;
    history: History;
    performanceReportWebApi: PerformanceReportWebApi;
    messageDispatcher: MessageDispatcher;
    errorPresenter: ErrorPresenter;
    viewVisibilityStorage: ViewVisibilityStorage;
  }) {
    this.store = store;
    this.history = history;
    this.performanceReportWebApi = performanceReportWebApi;
    this.messageDispatcher = messageDispatcher;
    this.errorPresenter = errorPresenter;
    this.viewVisibilityStorage = viewVisibilityStorage;
  }

  prepareReporting = () => {
    const project = this.store.getState().project.project;
    if (project === null) {
      throw new Error('project not found');
    }

    const performanceReports = project.performanceReports.reduce((map, report) => {
      if (report.adAccount.network == 'yahoo') {
        return map;
      }
      return map.set(report.id, {
        report,
        request: {
          campaignId: null,
          period: report.periodPatterns[0],
          metric: 'cost_per_action',
          target: report.comparisonTargets[0],
        },
      });
    }, new Map<string, { report: PerformanceReport; request: PerformanceReportSummaryRequest }>());

    const localProject = JSON.parse(window.localStorage.getItem(`project_brief_${project.id}`) || '{}');
    const hasLocalData = !!Object.keys(localProject).length;

    this.store.dispatch(setPerformanceReports(performanceReports));
    this.store.dispatch(setHasLocalProject(hasLocalData));
    performanceReports.forEach((item) => {
      this.preparePeriodicImprovementPerformance(item.report, item.request);
      this.prepareDailySummaryPerformance(item.report, item.request);
      this.preparePeriodicDetailPerformance(item.report, {
        period: item.request.period,
        campaignId: item.request.campaignId,
      });
    });
  };

  public prepareDailySummaryPerformance = async (
    report: PerformanceReport,
    request: PerformanceReportSummaryRequest,
  ): Promise<void> => {
    const allRequests = ['cost_per_action', 'view_through_cost_per_action', 'click_through_rate'].map((metric) => {
      return {
        ...request,
        metric: metric as PerformanceReportMetric,
      };
    });

    this.store.dispatch(startFetchingDailySummaryPerformance(report));

    try {
      const performance = await Promise.all(
        allRequests.map((request) => this.performanceReportWebApi.getDailySummaryPerformance(report.id, request)),
      );
      this.store.dispatch(setDailySummaryPerformance(report, performance));
    } catch (err) {
      this.store.dispatch(finishFetchingDailySummaryPerformance(report));
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  public preparePeriodicDetailPerformance = async (
    report: PerformanceReport,
    request: PerformanceReportDetailRequest,
  ): Promise<void> => {
    this.store.dispatch(startFetchingPeriodicDetailPerformance(report));

    try {
      const performance = await this.performanceReportWebApi.getPeriodicDetailPerformance(report.id, request);
      // sort
      performance.creatives.sort((a, b): number => {
        if (a.creation === null && b.creation !== null) {
          return 1;
        } else if (a.creation !== null && b.creation === null) {
          return -1;
        } else {
          const sorted = [a.creativeId, b.creativeId].sort();
          return sorted[0] == b.creativeId ? 1 : -1;
        }
      });
      this.store.dispatch(setPeriodicDetailPerformance(report, performance));
    } catch (err) {
      this.store.dispatch(finishFetchingPeriodicDetailPerformance(report));
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  public preparePeriodicImprovementPerformance = async (
    report: PerformanceReport,
    request: PerformanceReportImprovementRequest,
  ): Promise<void> => {
    this.store.dispatch(startFetchingPeriodicImprovementPerformance(report));

    try {
      const _performance = await this.performanceReportWebApi.getPeriodicImprovementPerformance(report.id, request);
      const creatives = _performance.creatives.map((creative) => ({
        ...creative,
        values: {
          ...creative.values,
          all: computeMetricsValues(creative.values.all, 'all'),
          staticImage: computeMetricsValues(creative.values.staticImage, 'static_image'),
          video: computeMetricsValues(creative.values.video, 'video'),
        },
      }));
      const performance = { ..._performance, creatives };
      this.store.dispatch(setPeriodicImprovementPerformance(report, performance));
    } catch (err) {
      this.store.dispatch(finishFetchingPeriodicImprovementPerformance(report));
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  public disposeProjectReporing = (): void => {
    this.store.dispatch(disposeProjectAndCreations());
  };

  public setReportRequest = (report: PerformanceReport, request: PerformanceReportSummaryRequest): void => {
    const prevState = this.store.getState().projectReporting.performanceReports.get(report.id);
    if (prevState == null) {
      return;
    }
    this.store.dispatch(setReportingRequest(report, request));
    this.preparePeriodicImprovementPerformance(report, request);
    this.prepareDailySummaryPerformance(report, request);
    this.preparePeriodicDetailPerformance(report, {
      period: request.period,
      campaignId: request.campaignId,
    });
  };

  public requestExportingPerformanceReport = async (
    report: PerformanceReport,
    request: PerformanceReportDetailRequest,
    dimension: PerformanceReportDimension,
  ): Promise<void> => {
    try {
      await this.performanceReportWebApi.postExportPerformanceReportRequest(
        report.id,
        request.period,
        request.campaignId || null,
        dimension,
      );
      this.messageDispatcher.notifyUnimportant('message.export_performance_report_request_has_succeeded', {});
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('message.export_performance_report_request_has_failed'));
    }
  };

  public isIntegrationBannerVisible = (adPlatform: PerformanceReportNetwork) => {
    const visibility = this.viewVisibilityStorage.getVisibility(this.getIntegrationBannerViewId(adPlatform));
    // true by default
    return visibility ? visibility === 'visible' : true;
  };

  public hideIntegrationBanner = (adPlatform: PerformanceReportNetwork) => {
    this.viewVisibilityStorage.saveVisibility(this.getIntegrationBannerViewId(adPlatform), 'hidden');
  };

  public goCreativeAnalyzeListPage = () => {
    const project = this.store.getState().project.project;
    if (project === null) {
      throw new Error('project not found');
    }
    const adVideos = project.adVideos;
    if (adVideos !== undefined) {
      const videoIds = adVideos
        .filter((video) => video.analyzingState === 'processed')
        .map((video) => video.adVideoId)
        .join(',');
      this.history.push(`/ad-creatives?creative=${videoIds}`);
    }
  };

  public toEditPage = () => {
    const project = this.store.getState().project.project;
    if (project === null) {
      throw new Error('project not found');
    }
    this.history.push(`/projects/${project.id}/edit?step=submitted`);
  };

  private getIntegrationBannerViewId = (adPlatform: PerformanceReportNetwork) =>
    `project.reporting.integration_banner.${adPlatform}`;
}

export default ShowPerformanceReportUsecase;
